/**
 * The document head per route: what a search engine or an AI agent reads.
 *
 * Only the three public pages are indexable. Every other path — the staff
 * screens behind `requireSession`, `/` (the signed-in home; anonymous visits
 * are redirected to `/discover`) and an unknown address — gets
 * `noindex, nofollow` and loses the canonical link and the structured data.
 * The rule is an allow-list on purpose: a staff route added later is private
 * without anybody remembering this file.
 *
 * Canonical URLs and JSON-LD use `location.origin` at runtime. The production
 * domain is not decided, and nothing here may name one.
 *
 * The copy is typed per locale like `OPS_COPY`, so a missing translation fails
 * the typecheck.
 */
import { HTML_LANG, type Locale } from '@playerone/api/i18n';

type Page = 'discover' | 'privacy' | 'login';
const PAGES: Record<string, Page> = { '/discover': 'discover', '/privacy': 'privacy', '/login': 'login' };

export const SEO_COPY: Record<Locale, Record<Page, { title: string; description: string }>> = {
  en: {
    discover: {
      title: 'PlayerOne · Record everyday activity, paid per reviewed minute',
      description: 'PlayerOne connects collectors in Vietnam with tasks to record everyday activity on the Ego head-worn camera. A person reviews every recording, and collectors are paid per reviewed minute.',
    },
    privacy: {
      title: 'Privacy (draft) · PlayerOne',
      description: 'Draft privacy notes for the PlayerOne landing page, its on-page demo and the signed-in demo studio. It does not replace the notice required for real data collection.',
    },
    login: {
      title: 'Sign in · PlayerOne console',
      description: 'Sign-in for PlayerOne staff: upload centre operators and footage reviewers. Collectors use the PlayerOne app, not this page.',
    },
  },
  vi: {
    discover: {
      title: 'PlayerOne · Ghi hình việc thường ngày, nhận tiền theo phút được duyệt',
      description: 'PlayerOne kết nối người thu thập tại Việt Nam với các nhiệm vụ ghi hình hoạt động thường ngày bằng camera đeo đầu Ego. Mỗi bản ghi đều được người thật duyệt, và người thu thập được trả theo số phút được duyệt.',
    },
    privacy: {
      title: 'Quyền riêng tư (bản nháp) · PlayerOne',
      description: 'Bản nháp về quyền riêng tư cho trang giới thiệu PlayerOne, bản demo trên trang và Phòng thử nghiệm sau đăng nhập. Bản này chưa thay thế thông báo cần có cho việc thu thập dữ liệu thật.',
    },
    login: {
      title: 'Đăng nhập · Bảng điều khiển PlayerOne',
      description: 'Trang đăng nhập cho nhân viên PlayerOne: người vận hành trung tâm tải lên và người duyệt video. Người thu thập dùng ứng dụng PlayerOne, không dùng trang này.',
    },
  },
  zh: {
    discover: {
      title: 'PlayerOne · 拍摄日常活动，按审核分钟计酬',
      description: 'PlayerOne 为越南采集员提供使用 Ego 头戴式相机拍摄日常活动的任务。每段录像都由人工审核，采集员按审核通过的分钟数获得报酬。',
    },
    privacy: {
      title: '隐私（草案）· PlayerOne',
      description: 'PlayerOne 公开介绍页面、页面内演示及登录后演示工作室的隐私说明草案，不替代真实数据采集所需的隐私通知。',
    },
    login: {
      title: '登录 · PlayerOne 控制台',
      description: 'PlayerOne 工作人员登录页面，供上传中心操作员和视频审核员使用。采集员请使用 PlayerOne 应用，而非此页面。',
    },
  },
};

function add<K extends 'meta' | 'link' | 'script'>(name: K, attrs: Partial<HTMLElementTagNameMap[K]>) {
  document.head.append(Object.assign(document.createElement(name), attrs));
}

export function applyHead(pathname: string, locale: Locale) {
  const path = pathname.replace(/(.)\/$/, '$1');
  const page = PAGES[path];
  document.head.querySelectorAll('meta[name="robots"], link[rel="canonical"], script[type="application/ld+json"]').forEach((el) => el.remove());
  if (!page) {
    document.title = 'PlayerOne';
    add('meta', { name: 'robots', content: 'noindex, nofollow' });
    return;
  }
  const { title, description } = SEO_COPY[locale][page];
  const origin = window.location.origin;
  document.title = title;
  const meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (meta) meta.content = description; else add('meta', { name: 'description', content: description });
  add('link', { rel: 'canonical', href: origin + path });
  add('script', {
    type: 'application/ld+json',
    textContent: JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'Organization', '@id': `${origin}/#organization`, name: 'PlayerOne', url: `${origin}/`, logo: `${origin}/mark.svg` },
        { '@type': 'WebSite', '@id': `${origin}/#website`, name: 'PlayerOne', url: `${origin}/`, inLanguage: HTML_LANG[locale], publisher: { '@id': `${origin}/#organization` } },
      ],
    }),
  });
}
